import {
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { HeaderComponent } from "@components/header/header.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { MarknadsomradeDTO } from "~app/common/models/common/MarknadsomradeDTO";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationContext } from "~app/common/validation/validationContext";
import { ValidationRuleDirective } from "../../../../../common/validation/validation-rule.directive";
import { ITplSearchArbetsorderQueryResult, TplSearchArbetsorderQuery } from "../../models/queries";
import { TplService } from "../../services/tpl.service";
import { TplStateService } from "../../state/tpl-state.service";
import { TplSearchValidation } from "../../validation/tpl-search-validation";

@Component({
    selector: "rask-tpl-search",
    templateUrl: "./tpl-search.component.html",
    styleUrls: ["./tpl-search.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        HeaderComponent,
        FormsModule,
        ReactiveFormsModule,
        TreeSelectorComponent,
        ValidationRuleDirective,
        RadiobuttonGroupComponent,
        DatepickerComponent,
        ButtonComponent,
        SubmitButtonComponent,
        TranslatePipe
    ]
})
export class TplSearchComponent implements OnInit, OnDestroy {
    marknadsomraden: SelectionItem[] = [];
    transportsatt: IRadiobuttonItem[] = [
        { key: "Rundvirke",
            value: "RV" },
        { key: "Bränsle",
            value: "Bränsle" }
    ];
    form = new UntypedFormGroup({
        marknadsomraden: new UntypedFormControl([]),
        transportsatt: new UntypedFormControl("Rundvirke"),
        periodType: new UntypedFormControl(0),
        periodFrom: new UntypedFormControl(),
        periodTom: new UntypedFormControl()
    });
    installningar: TplSearchArbetsorderQuery;
    noSearchResults: boolean;
    periodtypes: IRadiobuttonItem[] = [
        { key: 0,
            value: "Transportidspann" },
        { key: 1,
            value: "Periodsatt" },
        { key: 2,
            value: "Inget" }
    ];

    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output("result")

        result: EventEmitter<ITplSearchArbetsorderQueryResult> = new EventEmitter<ITplSearchArbetsorderQueryResult>();
    validationContext: ValidationContext;

    private readonly $translate = inject(TranslateService);

    private readonly WIK = "Transportplanering.search";
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly raskBusy = inject(BusyService);
    private readonly tplService = inject(TplService);
    private readonly tplStateService = inject(TplStateService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    private currentTransportsatt: string;

    addMonth() {
        this.form.get("periodFrom").setValue(DateUtils.formatDateString(DateUtils.now(), DateUtils.dateformat));
        this.form.get("periodTom").setValue(DateUtils.formatDateString(DateUtils.nowAddMonths(1), DateUtils.dateformat));
    }

    addQuarter() {
        this.form.get("periodFrom").setValue(DateUtils.now().toISOString());
        this.form.get("periodTom").setValue(DateUtils.nowAddMonths(3).toISOString());
    }

    getTidspanText() {
        return this.form.get("transportsatt").value === "Rundvirke"
            ? this.$translate.instant("TPL_SOK_TRANSPORTTIDSPANN_RUNDVIRKE")
            : this.$translate.instant("TPL_SOK_TRANSPORTTIDSPANN_BRANSLE");
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit() {
        this.addMonth();
        this.installningar = new TplSearchArbetsorderQuery();
        this.tplService.getMarknadsomraden()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(res => {
                this.webinstallningarService.getAsync<TplSearchArbetsorderQuery>(this.WIK).then((installningar: TplSearchArbetsorderQuery) => {
                    if (installningar) {
                        this.installningar = new TplSearchArbetsorderQuery(installningar);
                    }
                    this.setValues();
                    this.initMarknadsomraden(res);
                    this.validationContext = TplSearchValidation.init(this.installningar);
                });
            });
        this.tplStateService.current.setTransportsatt({ bransle: false,
            rundvirke: true });
    }

    produktionsomradeChanged() {
        this.installningar.produktionsomradeIdList = this.getSelectedProduktionsomradeIdList(this.marknadsomraden);
        if (this.installningar.produktionsomradeIdList.length > 0) {
            this.validationContext.validate(true);
        }
    }

    search() {
        this.installningar.produktionsomradeIdList = this.getSelectedProduktionsomradeIdList(this.marknadsomraden);
        if (!this.validationContext.validate(true)) {
            return;
        }

        if (this.validationContext.validate(true)) {
            this.noSearchResults = false;
            const query = new TplSearchArbetsorderQuery(
                {
                    produktionsomradeIdList: this.installningar.produktionsomradeIdList,
                    transportsatt: this.form.get("transportsatt").value,
                    periodFrom: this.form.get("periodFrom").value,
                    periodTom: this.form.get("periodTom").value,
                    periodType: this.form.get("periodType").value
                }
            );

            this.webinstallningarService.set(this.WIK, query);
            this.tplStateService.clear();

            this.raskBusy
                .addWork(this.tplService.searchArbetsorder(query))
                .subscribe(res => {
                    if (res.arbetsorderList.length === 0) {
                        this.noSearchResults = true;
                    }
                    this.tplStateService.initialize(res);
                    this.setTransportsatt(this.form.get("transportsatt").value);
                });
        }
    }

    private getSelectedProduktionsomradeIdList(items: SelectionItem[]): number[] {
        const ids: number[] = [];

        items.forEach(mo => {
            mo.children.forEach(ro => {
                ro.children
                    .filter(po => po.selected)
                    .forEach(po => {
                        ids.push(po.id);
                    });
            });
        });

        return ids;
    }

    private initMarknadsomraden(raw: MarknadsomradeDTO[]) {
        this.marknadsomraden = raw.map(marknadsOmrade => ({
            id: marknadsOmrade.id,
            text: marknadsOmrade.namn,
            children: marknadsOmrade.children.map(resultatOmrade => ({
                id: resultatOmrade.id,
                text: resultatOmrade.namn,
                children: resultatOmrade.children.map(produktionsOmrade => ({
                    id: produktionsOmrade.id,
                    text: produktionsOmrade.namn
                }))
            }))
        } as SelectionItem));

        this.setSelectedMarknadsomraden();
    }

    private setSelectedMarknadsomraden() {
        if (!this.installningar?.produktionsomradeIdList || this.installningar.produktionsomradeIdList.length === 0) {
            return;
        }

        this.form.get("marknadsomraden").setValue(this.installningar.produktionsomradeIdList);

        this.marknadsomraden.forEach(mo => {
            let numberOfSelectedMo = 0;
            let isPartial = false;
            const nrOfChildrenMo = mo.children.length;
            mo.children.forEach(ro => {
                let numberOfSelectedRo = 0;
                ro.children
                    .forEach(po => {
                        if (this.installningar.produktionsomradeIdList.includes(po.id)) {
                            po.selected = true;
                            numberOfSelectedRo++;
                        }
                    });
                const nrOfChildrenRo = ro.children.length;
                if (numberOfSelectedRo > 0 && numberOfSelectedRo === nrOfChildrenRo) {
                    ro.selected = true;
                    numberOfSelectedMo++;
                } else if (numberOfSelectedRo > 0) {
                    ro.partialSelected = true;
                    isPartial = true;
                }
            });
            mo.partialSelected = isPartial || numberOfSelectedMo > 0;
            mo.selected = numberOfSelectedMo === nrOfChildrenMo;
        });
    }

    private setTransportsatt(transportsatt: string) {
        this.currentTransportsatt = transportsatt;
        const curr = { bransle: this.currentTransportsatt === "Bränsle",
            rundvirke: this.currentTransportsatt === "Rundvirke" };
        this.tplStateService.current.setTransportsatt(curr);
    }

    private setValues() {
        this.form.get("periodFrom").setValue(ObjectUtils.hasValue(this.installningar.periodFrom)
            ? DateUtils.formatDateString(new Date(this.installningar.periodFrom), DateUtils.dateformat)
            : null);
        this.form.get("periodTom").setValue(ObjectUtils.hasValue(this.installningar.periodTom)
            ? DateUtils.formatDateString(new Date(this.installningar.periodTom), DateUtils.dateformat)
            : null);
        this.form.get("periodType").setValue(this.installningar.periodType);
        this.form.get("transportsatt").setValue(this.installningar.transportsatt);
        this.setTransportsatt(this.installningar.transportsatt);
    }
}
